import { useEffect, useRef, useState } from 'react';
import './App.css';

const logoUrl = new URL('../public/ibautilib-logoo.png', import.meta.url).toString();
const communityGroupUrl = new URL('../public/imagen-1.jpg', import.meta.url).toString();
const communityActivityUrl = new URL('../public/imagen-2-vertical.jpg', import.meta.url).toString();
const communityServiceUrl = new URL('../public/imagen-3.jpg', import.meta.url).toString();
const pastorsUrl = new URL('../public/pastores.jpg', import.meta.url).toString();

const whatsappMessage = encodeURIComponent(
  'Hola, quisiera recibir información sobre la Iglesia Bautista Libertad.',
);

const whatsappContacts = [
  { display: '+1 484-536-9765', href: `https://wa.me/14845369765?text=${whatsappMessage}` },
  { display: '+1 484-536-9764', href: `https://wa.me/14845369764?text=${whatsappMessage}` },
];

const mapUrl =
  'https://www.google.com/maps/search/?api=1&query=219%20N%2012th%20St%2C%20Allentown%2C%20PA';
const facebookUrl = 'https://www.facebook.com/ibautistalibertad1';

const navigation = [
  { label: 'Inicio', href: '#inicio' },
  { label: 'Nosotros', href: '#nosotros' },
  { label: 'Servicios', href: '#servicios' },
  { label: 'Comunidad', href: '#comunidad' },
  { label: 'Visítanos', href: '#visitanos' },
];

export function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('inicio');
  const contactDialog = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.13 },
    );

    const elements = document.querySelectorAll('.reveal');
    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const sections = document.querySelectorAll<HTMLElement>('main section[id]');
    const sectionObserver = new IntersectionObserver(
      (entries) => {
        const visibleSection = entries
          .filter((entry) => entry.isIntersecting)
          .sort((first, second) => second.intersectionRatio - first.intersectionRatio)[0];

        if (visibleSection) setActiveSection(visibleSection.target.id);
      },
      {
        rootMargin: '-28% 0px -58% 0px',
        threshold: [0, 0.15, 0.35],
      },
    );

    sections.forEach((section) => sectionObserver.observe(section));
    return () => sectionObserver.disconnect();
  }, []);

  useEffect(() => {
    document.body.classList.toggle('menu-is-open', menuOpen);

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };

    window.addEventListener('keydown', closeOnEscape);
    return () => {
      document.body.classList.remove('menu-is-open');
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [menuOpen]);

  const openContactDialog = () => {
    setMenuOpen(false);
    contactDialog.current?.showModal();
  };

  const closeMenu = () => setMenuOpen(false);

  return (
    <>
      <a className="skip-link" href="#main-content">
        Saltar al contenido principal
      </a>

      <header className="site-header">
        <div className="header-inner page-shell">
          <a className="brand" href="#inicio" aria-label="Iglesia Bautista Libertad, inicio">
            <img src={logoUrl} alt="Iglesia Bautista Libertad" width="797" height="395" />
          </a>

          <nav className="desktop-nav" aria-label="Navegación principal">
            {navigation.map((item) => (
              <a
                key={item.href}
                href={item.href}
                aria-current={activeSection === item.href.slice(1) ? 'page' : undefined}
              >
                {item.label}
              </a>
            ))}
          </nav>

          <a className="button button-primary header-cta" href="#visitanos">
            Planifica tu visita
          </a>

          <button
            className="menu-toggle"
            type="button"
            aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'}
            aria-expanded={menuOpen}
            aria-controls="mobile-navigation"
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span />
            <span />
          </button>
        </div>
      </header>

      <div
        id="mobile-navigation"
        className={`mobile-menu ${menuOpen ? 'is-open' : ''}`}
        aria-hidden={!menuOpen}
      >
        <nav aria-label="Navegación móvil">
          {navigation.map((item, index) => (
            <a
              key={item.href}
              href={item.href}
              onClick={closeMenu}
              tabIndex={menuOpen ? 0 : -1}
              aria-current={activeSection === item.href.slice(1) ? 'page' : undefined}
            >
              <span>0{index + 1}</span>
              {item.label}
            </a>
          ))}
        </nav>
        <div className="mobile-menu-actions">
          <button
            className="button button-primary"
            type="button"
            onClick={openContactDialog}
            tabIndex={menuOpen ? 0 : -1}
          >
            Escríbenos por WhatsApp
          </button>
          <p>Domingos 2:00 p. m. · Viernes 7:00 p. m.</p>
        </div>
      </div>

      <main id="main-content">
        <section className="hero" id="inicio" aria-labelledby="hero-title">
          <div className="hero-orb hero-orb-one" aria-hidden="true" />
          <div className="hero-orb hero-orb-two" aria-hidden="true" />

          <div className="hero-grid page-shell">
            <div className="hero-copy reveal is-visible">
              <p className="eyebrow"><span /> Fe que abraza. Comunidad que acompaña.</p>
              <h1 id="hero-title">Un lugar para crecer en fe, familia y propósito.</h1>
              <p className="hero-lead">
                Una comunidad donde puedes crecer en la Palabra y caminar acompañado.
                Aquí hay un lugar para ti.
              </p>

              <div className="hero-actions">
                <a className="button button-primary" href="#visitanos">
                  Planifica tu visita <span aria-hidden="true">→</span>
                </a>
                <button className="button button-secondary" type="button" onClick={openContactDialog}>
                  Escríbenos
                </button>
              </div>

              <div className="welcome-note">
                <span className="welcome-mark" aria-hidden="true">“</span>
                <p>Tu visita será grata.</p>
              </div>
            </div>

            <div className="hero-visual reveal is-visible">
              <div className="hero-photo-wrap">
                <img
                  className="hero-photo"
                  src={communityGroupUrl}
                  alt="Congregación de Iglesia Bautista Libertad reunida en comunidad"
                  width="1600"
                  height="1200"
                />
                <div className="hero-photo-label">
                  <span className="status-dot" aria-hidden="true" />
                  <div>
                    <strong>Te esperamos</strong>
                    <small>219 N 12th St · Allentown</small>
                  </div>
                </div>
              </div>
              <div className="hero-caption" aria-hidden="true">
                <span>IBL</span>
                <p>Una iglesia<br />para toda la familia</p>
              </div>
            </div>
          </div>
        </section>

        <section className="schedule-strip" id="servicios" aria-labelledby="schedule-title">
          <div className="page-shell schedule-layout">
            <div className="schedule-intro reveal">
              <p className="section-kicker">Encuéntranos esta semana</p>
              <h2 id="schedule-title">Hay un lugar para ti.</h2>
            </div>

            <article className="schedule-card reveal">
              <div className="schedule-day">
                <span className="status-dot" aria-hidden="true" />
                Domingo
              </div>
              <time>2:00 <small>p. m.</small></time>
              <p>Servicio dominical</p>
              <span className="presence-pill">Presencial</span>
            </article>

            <article className="schedule-card reveal">
              <div className="schedule-day">
                <span className="status-dot" aria-hidden="true" />
                Viernes
              </div>
              <time>7:00 <small>p. m.</small></time>
              <p>Estudio bíblico</p>
              <span className="presence-pill">Presencial</span>
            </article>
          </div>
        </section>

        <section className="about section-space" id="nosotros" aria-labelledby="about-title">
          <div className="page-shell about-grid">
            <div className="about-heading reveal">
              <p className="section-kicker">Bienvenido a casa</p>
              <h2 id="about-title">Una comunidad para crecer juntos.</h2>
            </div>
            <div className="about-copy reveal">
              <p>
                En Iglesia Bautista Libertad nos reunimos para conocer más de Dios,
                fortalecer nuestra fe y compartir la vida en comunidad.
              </p>
              <p>
                Seas nuevo en la fe o lleves años caminando con Jesús, queremos recibirte
                con cercanía, respeto y alegría.
              </p>
              <a className="text-link" href="#visitanos">Conoce cómo visitarnos <span aria-hidden="true">↗</span></a>
            </div>
          </div>

          <div className="page-shell visit-steps reveal" aria-labelledby="first-visit-title">
            <div className="steps-heading">
              <p className="section-kicker">¿Primera vez aquí?</p>
              <h3 id="first-visit-title">Tu primera visita puede ser sencilla.</h3>
            </div>
            <ol className="steps-list">
              <li>
                <span>01</span>
                <div><strong>Conoce nuestros horarios.</strong><p>Elige el encuentro que mejor funcione para ti.</p></div>
              </li>
              <li>
                <span>02</span>
                <div><strong>Encuentra cómo llegar.</strong><p>Abre la ubicación y planifica tu recorrido.</p></div>
              </li>
              <li>
                <span>03</span>
                <div><strong>Ven como eres.</strong><p>Estaremos felices de darte la bienvenida.</p></div>
              </li>
            </ol>
          </div>
        </section>

        <section className="community section-space" id="comunidad" aria-labelledby="community-title">
          <div className="page-shell community-heading reveal">
            <div>
              <p className="section-kicker">Nuestra comunidad</p>
              <h2 id="community-title">La fe también se vive acompañados.</h2>
            </div>
            <p>
              Compartimos, aprendemos y crecemos como una familia que hace espacio para cada generación.
            </p>
          </div>

          <div className="page-shell gallery-grid">
            <figure className="gallery-item gallery-tall reveal">
              <img
                src={communityActivityUrl}
                alt="Familias y niños participando en una actividad comunitaria"
                width="1536"
                height="2048"
                loading="lazy"
              />
              <figcaption><span>01</span> Crecemos juntos</figcaption>
            </figure>
            <div className="gallery-message reveal">
              <span className="gallery-cross" aria-hidden="true">+</span>
              <p>Fe que se comparte.<br />Vidas que se acompañan.</p>
            </div>
            <figure className="gallery-item gallery-offset reveal">
              <img
                src={communityServiceUrl}
                alt="Niños y voluntarios compartiendo una actividad creativa"
                width="1536"
                height="2048"
                loading="lazy"
              />
              <figcaption><span>02</span> Servimos con alegría</figcaption>
            </figure>
          </div>
        </section>

        <section className="pastors section-space" aria-labelledby="pastors-title">
          <div className="page-shell pastors-card reveal">
            <div className="pastors-photo">
              <img
                src={pastorsUrl}
                alt="Los pastores Salvador Ramírez y Juana Mora de Iglesia Bautista Libertad"
                width="1113"
                height="1011"
                loading="lazy"
              />
            </div>
            <div className="pastors-copy">
              <p className="section-kicker">Nuestros pastores</p>
              <h2 id="pastors-title">Liderando con fe, servicio y cercanía.</h2>
              <p className="pastors-names">Salvador Ramírez <span aria-hidden="true">&amp;</span> Juana Mora</p>
              <p>
                Nuestro deseo es que cada persona encuentre un espacio para conocer a Dios,
                crecer en su Palabra y construir relaciones significativas.
              </p>
              <div className="pastors-signoff">
                <span aria-hidden="true" />
                <p><strong>Será una alegría recibirte.</strong><br />Iglesia Bautista Libertad</p>
              </div>
            </div>
          </div>
        </section>

        <section className="visit section-space" id="visitanos" aria-labelledby="visit-title">
          <div className="page-shell visit-card reveal">
            <div className="visit-copy">
              <p className="section-kicker section-kicker-light">Planea tu visita</p>
              <h2 id="visit-title">Nos encantará conocerte.</h2>
              <p>
                Guarda el horario, abre la ubicación o escríbenos antes de venir.
                Queremos hacer tu primera visita lo más sencilla posible.
              </p>
              <div className="visit-actions">
                <a className="button button-light" href={mapUrl} target="_blank" rel="noreferrer">
                  Cómo llegar <span aria-hidden="true">↗</span>
                </a>
                <button className="button button-outline-light" type="button" onClick={openContactDialog}>
                  Hablar por WhatsApp
                </button>
                <a
                  className="button button-outline-light"
                  href={facebookUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Visítanos en Facebook <span aria-hidden="true">↗</span>
                </a>
              </div>
            </div>

            <div className="visit-details">
              <div className="detail-item">
                <span className="detail-number">01</span>
                <div><small>Dirección</small><strong>219 N 12th St<br />Allentown, PA</strong></div>
              </div>
              <div className="detail-item">
                <span className="detail-number">02</span>
                <div><small>Domingo</small><strong>2:00 p. m.</strong></div>
              </div>
              <div className="detail-item">
                <span className="detail-number">03</span>
                <div><small>Viernes</small><strong>7:00 p. m.</strong></div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="page-shell footer-main">
          <div className="footer-brand">
            <a href="#inicio" aria-label="Volver al inicio">
              <img src={logoUrl} alt="Iglesia Bautista Libertad" width="797" height="395" />
            </a>
            <p>Fe, familia y propósito en el corazón de Allentown.</p>
          </div>
          <div className="footer-links">
            <strong>Explora</strong>
            {navigation.slice(1).map((item) => <a key={item.href} href={item.href}>{item.label}</a>)}
          </div>
          <div className="footer-contact">
            <strong>Conversemos</strong>
            <a href={facebookUrl} target="_blank" rel="noreferrer">Facebook</a>
            {whatsappContacts.map((contact) => (
              <a key={contact.href} href={contact.href} target="_blank" rel="noreferrer">
                {contact.display}
              </a>
            ))}
          </div>
        </div>
        <div className="page-shell footer-bottom">
          <p>© {new Date().getFullYear()} Iglesia Bautista Libertad.</p>
          <p>Todos son bienvenidos.</p>
        </div>
      </footer>

      <dialog className="contact-dialog" ref={contactDialog} aria-labelledby="contact-dialog-title">
        <button
          className="dialog-close"
          type="button"
          aria-label="Cerrar contactos"
          onClick={() => contactDialog.current?.close()}
        >
          ×
        </button>
        <p className="section-kicker">Estamos para ayudarte</p>
        <h2 id="contact-dialog-title">¿A cuál número deseas escribir?</h2>
        <p>Ambos contactos están disponibles para orientarte y responder tus preguntas.</p>
        <div className="dialog-options">
          {whatsappContacts.map((contact, index) => (
            <a key={contact.href} href={contact.href} target="_blank" rel="noreferrer">
              <span>WhatsApp 0{index + 1}</span>
              <strong>{contact.display}</strong>
              <b aria-hidden="true">↗</b>
            </a>
          ))}
        </div>
      </dialog>
    </>
  );
}
